import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { portalApi, clearPortalToken, isPreview } from './portalApi';
import { getPortalIcon } from './portalIcons';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Loader2, LogOut, Menu, Eye, ExternalLink, Sparkles } from 'lucide-react';
import { useHeadway, openHeadway } from './headway';
import { PortalReportsProvider } from './PortalReportsContext';
import { MetricsMenuIndicator } from './PortalReportsStatus';

const NEWS_CLS = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left text-slate-300 hover:bg-slate-800 hover:text-white';

const NewsItem = ({ variant, unseen, onMobileOpen }) => {
  if (variant === 'desktop') {
    return (
      <button type="button" id="headway-trigger" className={NEWS_CLS} data-testid="portal-news-item">
        <Sparkles size={18} className="shrink-0" />
        <span className="truncate flex-1">Novidades</span>
        <span id="headway-badge" className="shrink-0 inline-flex h-[18px] items-center" data-testid="portal-news-badge" />
      </button>
    );
  }
  return (
    <button type="button" onClick={onMobileOpen} className={NEWS_CLS} data-testid="portal-news-item-mobile">
      <Sparkles size={18} className="shrink-0" />
      <span className="truncate flex-1">Novidades</span>
      {unseen > 0 && (
        <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#5B5BD6] text-white text-[11px] leading-[18px] text-center font-mono" data-testid="portal-news-badge-mobile">{unseen}</span>
      )}
    </button>
  );
};

const PortalMenu = ({ project, items, activeKey, onNavigate, news }) => (
  <div className="flex flex-col h-full">
    <div className="px-5 py-6 border-b border-slate-800">
      <p className="text-xs uppercase tracking-wider text-slate-400">Portal do Cliente</p>
      <h2 className="text-lg font-bold text-white mt-1 truncate" data-testid="portal-project-name">{project?.name || 'Projeto'}</h2>
    </div>
    <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
      {items.length === 0 && (
        <p className="px-3 py-2 text-sm text-slate-400">Nenhum item disponível ainda.</p>
      )}
      {items.map((item) => {
        const Icon = getPortalIcon(item.icon);
        const isExternal = item.type === 'link_externo';
        const active = !isExternal && activeKey === item.key;
        return (
          <button
            key={item.id}
            onClick={() => onNavigate(item)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
            data-testid={`portal-menu-${isExternal ? 'link' : item.key}-${item.id}`}
          >
            <Icon size={18} className="shrink-0" />
            <span className="truncate flex-1">{item.label}</span>
            {isExternal && <ExternalLink size={14} className="shrink-0 opacity-60" />}
            {!isExternal && item.key === 'metricas' && <MetricsMenuIndicator active={active} />}
          </button>
        );
      })}
      {news && <NewsItem {...news} />}
    </nav>
    <div className="px-5 py-4 border-t border-slate-800">
      <p className="text-xs text-slate-400">por <span className="font-semibold text-slate-200">Proex Digital</span></p>
    </div>
  </div>
);

export const PortalLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, me: null });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const preview = isPreview();
  const newsEnabled = !preview && !state.loading && !!state.me;
  useHeadway(newsEnabled, setUnseen);
  const mainRef = useRef(null);
  useEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [location.pathname]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await portalApi.get('/api/portal/auth/me');
        if (!active) return;
        if (!preview && data.user?.must_change_password) {
          navigate('/portal/trocar-senha');
          return;
        }
        setState({ loading: false, me: data });
      } catch (err) {
        if (!active) return;
        if (preview) {
          setState({ loading: false, me: null });
        } else {
          clearPortalToken();
          navigate('/portal/login');
        }
      }
    })();
    return () => { active = false; };
  }, [navigate, preview]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const me = state.me || {};
  const items = me.menu_items || [];
  const activeKey = location.pathname.split('/portal/')[1] || '';

  const handleNavigate = (item) => {
    setMobileOpen(false);
    if (item.type === 'link_externo' && item.url) {
      window.open(item.url, '_blank', 'noopener');
    } else {
      navigate(`/portal/${item.key}`);
    }
  };

  const logout = () => { clearPortalToken(); navigate('/portal/login'); };

  const openNewsMobile = () => {
    setMobileOpen(false);
    setTimeout(openHeadway, 300);
  };
  const desktopNews = newsEnabled ? { variant: 'desktop' } : null;
  const mobileNews = newsEnabled ? { variant: 'mobile', unseen, onMobileOpen: openNewsMobile } : null;

  return (
    <PortalReportsProvider key={me.project?.id} projectId={me.project?.id} autoPrefetch={!preview}>
    <div className="h-screen h-dvh overflow-hidden flex bg-slate-50" data-testid="portal-layout">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 h-full bg-slate-900 flex-col" data-testid="portal-sidebar">
        <PortalMenu project={me.project} items={items} activeKey={activeKey} onNavigate={handleNavigate} news={desktopNews} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Top bar */}
        <header className="h-14 shrink-0 bg-white border-b flex items-center justify-between px-4 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="portal-mobile-menu-trigger">
                  <Menu size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-64 bg-slate-900 border-slate-800">
                <PortalMenu project={me.project} items={items} activeKey={activeKey} onNavigate={handleNavigate} news={mobileNews} />
              </SheetContent>
            </Sheet>
            <span className="font-semibold truncate md:hidden">{me.project?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {preview && (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full" data-testid="portal-preview-badge">
                <Eye size={13} /> Pré-visualização
              </span>
            )}
            {!preview && (
              <Button variant="ghost" size="sm" onClick={logout} data-testid="portal-logout-button">
                <LogOut size={16} className="mr-1" /> Sair
              </Button>
            )}
          </div>
        </header>

        <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto p-5 md:p-8" data-testid="portal-main">
          <Outlet context={{ me }} />
        </main>
      </div>
    </div>
    </PortalReportsProvider>
  );
};
